import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader2, ShieldCheck, Smartphone, CreditCard, Landmark } from 'lucide-react';
import { formatINR } from '@/lib/format';
import { Logo } from '@/components/motion/Primitives';

// Demo payment sheet used when Razorpay keys are not configured.
// Clearly labelled as demo. Simulates a successful capture.
export default function DemoPaymentModal({ open, onClose, amount, orderNumber, onPaid }) {
    const [processing, setProcessing] = useState(false);

    const pay = async () => {
        setProcessing(true);
        setTimeout(async () => {
            await onPaid();
            setProcessing(false);
        }, 1400);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o && !processing) onClose(); }}>
            <DialogContent data-testid="demo-payment-modal" className="sm:max-w-md p-0 overflow-hidden">
                <div className="royal-panel px-6 py-5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Logo variant="dark" className="h-5" />
                            <div className="pl-3 border-l border-white/20">
                                <p className="text-white font-display font-semibold leading-none">Secure Pay</p>
                                <p className="text-white/60 text-xs mt-1">Order {orderNumber}</p>
                            </div>
                        </div>
                        <span className="text-[11px] uppercase tracking-wide bg-[var(--brand-yellow)] text-[var(--ink)] font-bold px-2 py-1 rounded">Demo</span>
                    </div>
                </div>
                <DialogHeader className="px-6 pt-5">
                    <DialogTitle className="font-display text-2xl">{formatINR(amount)}</DialogTitle>
                    <DialogDescription className="sr-only">Complete your payment for order {orderNumber} in demo mode.</DialogDescription>
                </DialogHeader>
                <div className="px-6 pb-6">
                    <p className="text-sm text-[var(--ink-muted)]">Choose a payment method to complete your order.</p>
                    <div className="mt-4 grid grid-cols-3 gap-2">
                        {[{ icon: Smartphone, label: 'UPI' }, { icon: CreditCard, label: 'Card' }, { icon: Landmark, label: 'Netbanking' }].map((m, i) => (
                            <div key={i} className={`rounded-xl border p-3 flex flex-col items-center gap-1.5 ${i === 0 ? 'border-[var(--brand-blue)] bg-[var(--surface-2)]' : 'border-border'}`}>
                                <m.icon className="h-5 w-5 text-[var(--brand-blue)]" />
                                <span className="text-xs font-medium">{m.label}</span>
                            </div>
                        ))}
                    </div>
                    <button data-testid="demo-pay-button" onClick={pay} disabled={processing} className="btn-blue w-full h-12 rounded-xl font-semibold mt-5 inline-flex items-center justify-center gap-2 disabled:opacity-80">
                        {processing ? (<><Loader2 className="h-5 w-5 animate-spin" /> Processing…</>) : (<>Pay {formatINR(amount)}</>)}
                    </button>
                    <p className="mt-3 text-xs text-[var(--ink-muted)] flex items-center justify-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Demo mode — no real money is charged.</p>
                </div>
            </DialogContent>
        </Dialog>
    );
}
